import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Button,
  
} from '@mui/material';

import { RootState, selectApplications } from '../store';
import { LoanApplication } from '../types';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';

const ApplicationList: React.FC = () => {
  const { t } = useTranslation();
  const applications = useSelector(selectApplications);
  const [filters, setFilters] = useState({
    name: '',
    status: '',
    nationalId: '',
    customerType: '',
  });

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const filteredApplications = useMemo(() => {
    // Ensure applications is an array before filtering
    if (!Array.isArray(applications)) {
      return [];
    }
    
    return applications.filter(app => {
      const nameMatch = !filters.name || (app.applicantName && app.applicantName.toLowerCase().includes(filters.name.toLowerCase()));
      const statusMatch = !filters.status || app.status === filters.status;
      const nationalIdMatch = !filters.nationalId || (app.nationalId && app.nationalId.toLowerCase().includes(filters.nationalId.toLowerCase()));
      const customerTypeMatch = !filters.customerType || app.customerType === filters.customerType;
      return nameMatch && statusMatch && nationalIdMatch && customerTypeMatch;
    });
  }, [applications, filters]);

  const getStatusChip = (status: LoanApplication['status']) => {
    let color: 'success' | 'error' | 'warning' = 'warning';
    if (status === 'approved') {
      color = 'success';
    } else if (status === 'rejected') {
      color = 'error';
    }
    return <Chip label={status} color={color} size="small" />;
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h4" gutterBottom>
        {t('applicationList.title')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            fullWidth
            label={t('applicationList.searchByName')}
            name="name"
            value={filters.name}
            onChange={handleFilterChange}
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
                fullWidth
                label={t('applicationList.searchByNationalId')}
                name="nationalId"
                value={filters.nationalId}
                onChange={handleFilterChange}
                variant="outlined"
                size="small"
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              label={t('applicationList.status')}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Customer Type</InputLabel>
                <Select
                    name="customerType"
                    value={filters.customerType}
                    onChange={handleFilterChange}
                    label={t('applicationList.customerType')}
                >
                    <MenuItem value=""><em>{t('applicationList.all')}</em></MenuItem>
                    <MenuItem value="individual">{t('loanForm.individual')}</MenuItem>
                    <MenuItem value="sme">{t('loanForm.sme')}</MenuItem>
                </Select>
            </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('applicationList.applicantName')}</TableCell>
              <TableCell>{t('applicationList.customerType')}</TableCell>
              <TableCell>{t('applicationList.email')}</TableCell>
              <TableCell>{t('applicationList.amountRequested')}</TableCell>
              <TableCell>{t('applicationList.score')}</TableCell>
              <TableCell>{t('applicationList.status')}</TableCell>
              <TableCell>{t('applicationList.interestRate')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredApplications.map((app) => (
              <TableRow 
                key={app.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <TableCell>{app.applicantName}</TableCell>
                <TableCell>{app.customerType}</TableCell>
                <TableCell>{app.email}</TableCell>
                <TableCell>{app.requestedAmount.toLocaleString('en-ZW')} ZiG</TableCell>
                <TableCell>{app.creditScore}</TableCell>
                <TableCell>{getStatusChip(app.status)}</TableCell>
                <TableCell>{app.interestRate ? `${app.interestRate}%` : 'N/A'}</TableCell>
                <TableCell>
                  <Button
                    component={Link}
                    to={`/application/${app.id}`}
                    variant="outlined"
                    size="small"
                  >
                    View Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ApplicationList; 